import * as API from '@/API';
import type FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { makeAutoObservable } from 'mobx';
import { type GeneralSearchModel, OrderSearchResult, PatientSearchResult } from '@/Models/GeneralSearch';

interface StateData {
    patients: PatientSearchResult[];
    orders: OrderSearchResult[];
    patientsOpen: boolean;
    ordersOpen: boolean;
    isLoaded: boolean;
}

export default class GeneralSearchStore {
    state: StateData = {
        patients: [],
        orders: [],
        patientsOpen: true,
        ordersOpen: true,
        isLoaded: false,
    };

    private readonly fcStore: FilingCabinetStore<StateData>;
    private readonly t: (str: string) => string;
    private readonly searchValue: string;

    constructor(fcStore: FilingCabinetStore<StateData>, useTranslation: (str: string) => string, searchValue: string) {
        this.fcStore = fcStore;
        this.t = useTranslation;
        this.searchValue = searchValue;

        makeAutoObservable(this);

        this.fcStore.initializeState(this.initOld, this.initNew);
    }

    private readonly initOld = (state: StateData): void => {
        this.setState({
            orders: state.orders,
            patients: state.patients,
            ordersOpen: state.ordersOpen,
            patientsOpen: state.patientsOpen,
            isLoaded: state.isLoaded,
        });
    };

    private readonly initNew = (): void => {
        this.setTitle(`${this.t('GeneralSearch')} - ${this.searchValue}`);
        this.setIsLoaded(false);
        this.setOrders([]);
        this.setPatients([]);
        void API.general.generalSearch(this.searchValue)
            .then((res: GeneralSearchModel) => {
                this.setPatients(res.Patients);
                this.setOrders(res.Orders);
            }).finally(() => {
                this.setIsLoaded(true);
                this.fcStore.updateState(this.state);
            });
    };

    private readonly setTitle = (title: string): void => {
        this.fcStore.updateTitle(`${title}`);
    };

    setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    setIsLoaded = (newState: boolean): void => {
        this.state.isLoaded = newState;
        this.fcStore.updateState(this.state);
    };

    setOrders = (orders: OrderSearchResult[]): void => {
        this.state.orders = orders;
        this.fcStore.updateState(this.state);
    };

    setPatients = (patients: PatientSearchResult[]): void => {
        this.state.patients = patients;
        this.fcStore.updateState(this.state);
    };

    setPatientsOpen = (val: boolean): void => {
        this.state.patientsOpen = val;
        this.fcStore.updateState(this.state);
    };

    setOrdersOpen = (val: boolean): void => {
        this.state.ordersOpen = val;
        this.fcStore.updateState(this.state);
    };
};
